import { ComponentProps } from "react";
import { Bar } from "react-chartjs-2";
import { ChartOptions, Scale } from "chart.js";
import { CHART_GRID_COLOR, CHART_TEXT_COLOR } from "../charts/registerCharts";
import styles from "../WorkoutStatistics.module.scss";

type BarData = ComponentProps<typeof Bar>["data"];

/** Top padding so the top Y-axis label isn't clipped (must match on both charts). */
const PAD_TOP = 8;

/** Width the pinned Y-axis column needs to fit its widest value label. */
const axisColumnWidth = (data: BarData): number => {
  const values = data.datasets.flatMap((dataset) =>
    (dataset.data as (number | null)[]).map((value) =>
      typeof value === "number" ? value : 0,
    ),
  );
  const maxValue = values.length ? Math.max(0, ...values) : 0;
  const chars = Math.round(maxValue).toLocaleString().length;
  // chars * ~7px per glyph + room for tick padding and a little left breathing space.
  return Math.min(64, Math.max(30, chars * 7 + 16));
};

interface IScrollableBarChartProps {
  data: BarData;
  /** Number of categories (bars / grouped bars) on the X axis. */
  count: number;
  /** Minimum horizontal space per category before the chart scrolls. */
  slotWidth?: number;
  /** Render an HTML legend (dataset label + colour) above the chart. */
  showLegend?: boolean;
}

// The axis chart and the bars chart must keep identical plot geometry so the
// pinned labels line up with the scrolling grid. We zero the scales' own
// overflow padding and drive the top inset purely from layout.padding so both
// charts compute the same plot top/bottom regardless of which axis they show.
const zeroAxisPadding = (scale: Scale) => {
  const s = scale as unknown as { paddingTop: number; paddingBottom: number };
  s.paddingTop = 0;
  s.paddingBottom = 0;
};

/**
 * A horizontally scrollable bar chart with a pinned Y-axis. The left column is a
 * tiny chart that draws only the value axis (and never scrolls); the right column
 * scrolls and draws the bars + grid lines with its own Y-axis hidden. Both share
 * the same data, so they compute the same scale, and the same plot insets, so the
 * pinned labels stay aligned with the scrolling grid. When the workouts fit, the
 * bars area simply fills the space and no scrollbar appears.
 */
export const ScrollableBarChart = ({
  data,
  count,
  slotWidth = 44,
  showLegend = false,
}: IScrollableBarChartProps) => {
  const axisWidth = axisColumnWidth(data);

  // Left chart: real Y labels (right-aligned next to the bars), bars + X hidden.
  // Forcing the axis to fill the whole (tight) column keeps the labels right-
  // aligned against the bars and guarantees they never clip.
  const axisOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: { padding: { top: PAD_TOP } },
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: "transparent", maxRotation: 0, autoSkip: true },
      },
      y: {
        beginAtZero: true,
        grid: { display: false },
        border: { display: false },
        ticks: { color: CHART_TEXT_COLOR },
        afterFit: (scale: Scale) => {
          scale.width = axisWidth;
          zeroAxisPadding(scale);
        },
      },
    },
  };

  // Right chart: bars + X labels + horizontal grid; Y axis hidden (width 0) so the
  // bars use the full width — the values are read off the pinned axis on the left.
  const barsOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: PAD_TOP } },
    plugins: { legend: { display: false } },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: CHART_TEXT_COLOR, maxRotation: 0, autoSkip: true },
      },
      y: {
        beginAtZero: true,
        grid: { color: CHART_GRID_COLOR, drawTicks: false },
        border: { display: false },
        ticks: { display: false },
        afterFit: (scale: Scale) => {
          scale.width = 0;
          zeroAxisPadding(scale);
        },
      },
    },
  };

  // Same data drives the axis chart (so the scale matches) but its bars are
  // invisible — only the Y labels are wanted from it.
  const axisData: BarData = {
    labels: data.labels,
    datasets: data.datasets.map((dataset) => ({
      ...dataset,
      backgroundColor: "transparent",
      borderColor: "transparent",
    })),
  };

  return (
    <div className={styles.sticky_chart_wrapper}>
      {showLegend && (
        <div className={styles.sticky_legend}>
          {data.datasets.map((dataset) => (
            <span className={styles.sticky_legend_item} key={String(dataset.label)}>
              <span
                className={styles.sticky_legend_dot}
                style={{ background: dataset.backgroundColor as string }}
              />
              {dataset.label}
            </span>
          ))}
        </div>
      )}
      <div className={styles.sticky_chart}>
        <div className={styles.sticky_axis} style={{ width: axisWidth }}>
          <Bar data={axisData} options={axisOptions} />
        </div>
        <div className={styles.sticky_scroll}>
          <div
            className={styles.sticky_scroll_inner}
            style={{ minWidth: `${count * slotWidth}px` }}
          >
            <Bar data={data} options={barsOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};
